import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, useLocation } from 'react-router-dom';

import NavBar from './components/navbar/NavBar_refactored';
import { LoadingOverlay, ScrollToTop } from './components/ui';
import ErrorBoundary from './components/ui/ErrorBoundary';
import { Splash } from './components/ui/Splash';
import { AppProvider, useAppContext } from './context/AppContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import AppRoutes from './routes/AppRoutes';
import { audioManager } from './utils/audioManager';
import smartDataCleaner from './utils/smartDataCleaner';

// 内部组件，用于在Router内部使用useLocation
function AppContentInner() {
  const { state } = useAppContext();
  const location = useLocation();

  // 檢查是否為登入或註冊頁面
  const isAuthPage =
    location.pathname === '/login' || location.pathname === '/register';

  // 檢查是否為Projection頁面（完全獨立）
  const isProjectionPage = location.pathname === '/projection';

  // Post a Poster 页面完全独立，无 NavBar
  const isPostPosterPage = location.pathname === '/post-poster';

  // 檢查是否為首頁
  const isHomePage = location.pathname === '/';

  // 🔧 【音乐控制】当离开登录/注册页面时，自动暂停音乐
  useEffect(() => {
    // 如果不是 Auth 页面，且音乐正在播放，则暂停
    if (!isAuthPage && audioManager.isPlaying()) {
      console.log('[App] Leaving auth page, pausing background music...');
      audioManager.pause();
    }
  }, [location.pathname, isAuthPage]);

  return (
    <div
      className={`min-h-screen ${
        isAuthPage
          ? 'bg-gray-950'
          : isProjectionPage
          ? 'bg-black'
          : isPostPosterPage
          ? 'bg-white'
          : 'bg-gray-50'
      }`}
    >
      {!isAuthPage && !isProjectionPage && !isPostPosterPage && <NavBar />}
      <main className={isAuthPage || isPostPosterPage ? '' : isHomePage ? '' : 'pt-16'}>
        <LoadingOverlay />
        <AppRoutes />
      </main>
    </div>
  );
}

// 应用内容组件
function AppContent() {
  const { state } = useAppContext();

  // 确保页面可以滚动
  useEffect(() => {
    // 恢复页面滚动
    document.body.style.overflow = 'unset';
    document.documentElement.style.overflow = 'unset';
  }, []);

  // 初始化智能数据清理器
  useEffect(() => {
    smartDataCleaner.init().catch(error => {
      console.warn('[App] Smart data cleaner initialization failed:', error);
    });
  }, []);

  // 如果应用未初始化，显示Splash
  if (!state.isInitialized) {
    return <Splash />;
  }

  // 应用已初始化，显示正常内容
  return (
    <ErrorBoundary>
      <Router>
        <ScrollToTop />
        <AppContentInner />
      </Router>
    </ErrorBoundary>
  );
}

function App() {
  return (
    <AuthProvider>
      <NotificationProvider>
        <AppProvider>
          <AppContent />
        </AppProvider>
      </NotificationProvider>
    </AuthProvider>
  );
}

export default App;
